
import { useState } from "react";
import { toast } from "sonner";
import { useLogoMutation } from "./mutations/updateCompanyLogo";
import { handleFileUpload } from "./handlers/fileUploadHandler";
import { handleUrlUpload } from "./handlers/urlUploadHandler";
import { logger } from "@/services/LoggingService";

const COMPONENT = 'LogoManagement';

interface UseLogoManagementProps {
  companyId: string;
  onLogoUpdate: (newLogoUrl: string | null) => void;
}

export function useLogoManagement({ companyId, onLogoUpdate }: UseLogoManagementProps) {
  if (!companyId) {
    throw new Error('Company ID is required for logo management');
  }

  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: updateLogo } = useLogoMutation(companyId);
  const [cacheKey, setCacheKey] = useState(Date.now());

  const getCachedLogoUrl = (logoUrl: string | null) => {
    if (!logoUrl) return null;
    try {
      // Normalize any render URLs to raw object URLs and strip transform params
      let normalized = logoUrl.replace('/storage/v1/render/image/public/', '/storage/v1/object/public/');
      if (normalized.includes('?')) {
        const url = new URL(normalized);
        ['width', 'format', 'quality', 'resize', 'crop'].forEach((p) => url.searchParams.delete(p));
        normalized = url.origin + url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '');
      }
      const sep = normalized.includes('?') ? '&' : '?';
      return `${normalized}${sep}t=${cacheKey}`;
    } catch {
      const sep = logoUrl.includes('?') ? '&' : '?';
      return `${logoUrl}${sep}t=${cacheKey}`;
    }
  };

  const refreshCache = () => {
    setCacheKey(Date.now());
  };

  const handleSuccess = async (publicUrl: string) => {
    await updateLogo(publicUrl);
    onLogoUpdate(publicUrl);
    refreshCache();
    toast.success("Logo updated successfully");
  };

  const handleLogoRemove = async (): Promise<boolean> => {
    try {
      logger.info(COMPONENT, 'Starting logo removal', { 
        companyId 
      });
      
      setIsUploading(true);
      await updateLogo(null);
      onLogoUpdate(null);
      refreshCache();

      logger.info(COMPONENT, 'Logo removal successful', { 
        companyId 
      });
      
      toast.success("Logo removed successfully");
      return true;
    } catch (error: any) {
      logger.error(COMPONENT, 'Logo removal failed', {
        error: error.message,
        details: error,
        companyId
      });
      
      toast.error(error.message || "Failed to remove logo");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    getCachedLogoUrl,
    handleFileUpload: (file: File) => handleFileUpload({ 
      companyId, 
      file, 
      onSuccess: handleSuccess,
      setIsUploading 
    }),
    handleLogoUrlUpdate: (logoUrl: string) => handleUrlUpload({ 
      companyId, 
      logoUrl, 
      onSuccess: handleSuccess,
      setIsUploading 
    }),
    handleLogoRemove
  };
}
